import { MONTHLY_CALENDAR } from '../lib/data';
import { getClimate, baselineMonthFor, ZONE_EMOJI } from '../lib/climate';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { ShedProvider } from '../lib/shed/ShedContext';
import { lazy, Suspense } from 'react';
const ShedCalendar = lazy(() => import('./shed/ShedCalendar'));

const MONTHS = {
    da: ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    sv: ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'],
    no: ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'],
};

export default function Kalender() {
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const current = new Date().getMonth() + 1;
    const months = MONTHS[locale] || MONTHS.en || MONTHS.da;

    // Region-aware: shift the Nordic-calibrated advice by the user's latitude.
    const lat = user?.location?.lat;
    const city = user?.location?.city;
    const climate = getClimate(lat);

    return (
        <>
        <ShedProvider owner={user?.user_id || 'device'} key={user?.user_id || 'device'}><Suspense fallback={<p role="status" className="p-6 text-brand-ink">{locale === 'da' ? 'Åbner kalender…' : 'Opening calendar…'}</p>}><ShedCalendar defaultScope="garden" /></Suspense></ShedProvider>
        <details className="px-5 pb-32 max-w-2xl mx-auto">
            <summary className="text-brand-ink p-4">{locale === 'da' ? 'Årets generelle havetips' : 'General gardening tips for the year'}</summary>
            <p data-testid="calendar-eyebrow" className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">
                {climate && city ? t('calendar.adjusted').replace('{city}', city) : t('calendar.sub')}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-4">{t('calendar.title')}</h1>

            {climate && (
                <span data-testid="calendar-zone-chip" className="inline-flex items-center gap-1.5 text-xs font-medium bg-brand-green/10 text-brand-green px-3 py-1.5 rounded-full mb-8">
                    <span>{ZONE_EMOJI[climate.zone]}</span>
                    {t(`calendar.zone_${climate.zone}`)}
                </span>
            )}
            {!climate && <div className="mb-8" />}

            <div className="space-y-3 stagger">
                {months.map((m, idx) => {
                    const i = idx + 1;
                    const srcMonth = climate ? baselineMonthFor(i, climate.shift) : i;
                    const tip = (MONTHLY_CALENDAR[srcMonth] || {})[locale] || MONTHLY_CALENDAR[srcMonth]?.en || MONTHLY_CALENDAR[srcMonth]?.da || '';
                    const isNow = i === current;
                    return (
                        <div
                            key={i}
                            data-testid={`calendar-month-${i}`}
                            className={`flex items-start gap-4 rounded-3xl border p-5 transition-all ${
                                isNow
                                    ? 'bg-brand-green text-brand-bg border-brand-green'
                                    : 'bg-brand-card border-brand-border'
                            }`}
                        >
                            <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 ${isNow ? 'bg-brand-bg/15 text-brand-bg' : 'bg-brand-bg2 text-brand-ink'}`}>
                                <span className="text-[9px] uppercase tracking-widest font-semibold opacity-70">{m.slice(0, 3)}</span>
                                <span className="font-serif text-xl">{i}</span>
                            </div>
                            <div className="flex-1">
                                <h3 className={`font-serif text-xl ${isNow ? 'text-brand-bg' : 'text-brand-ink'}`}>{m}</h3>
                                <p className={`text-sm mt-1 leading-relaxed ${isNow ? 'text-brand-bg/80' : 'text-brand-ink2'}`}>{tip}</p>
                                {isNow && <span className="inline-block mt-2 text-[10px] uppercase tracking-widest font-semibold bg-brand-bg/15 px-2 py-0.5 rounded-full">{t('calendar.now')}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </details>
        </>
    );
}
